/**
 * The env-name rule of a sandbox command — dependency-free on purpose: imported as `@papan/sandbox/env-names` by the
 * keybook (which resolves credentials onto env names) and the agent's routes without loading the pool or dockerode.
 */

/** kinds.envKeyRe — the env names the broker accepts (pinned to services/papan-sandboxd/internal/kinds/exec.go). */
export const SANDBOX_ENV_NAME_RE = /^[A-Za-z_][A-Za-z0-9_]{0,127}$/

/**
 * Env names reserved for the sandbox itself — never a credential's or a caller's (review round 4): PAPAN_EXEC_MARKER
 * is what a command is killed (and probed) by; a credential mapped to it would steer the kill, and its value would
 * travel as the marker.
 */
export const RESERVED_SANDBOX_ENV: ReadonlySet<string> = new Set(['PAPAN_EXEC_MARKER'])

/**
 * The exact shape of a per-binding name the platform generates for a hidden (git) credential —
 * `__PAPAN_CRED_<PROVIDER>_<up to 12 letters/digits>` (`hiddenCredEnvName`, features/agent/core/hidden-cred-env.ts;
 * the providers are its `HIDDEN_PROVIDER_HOSTS`, pinned by a test there).
 */
export const GENERATED_HIDDEN_CRED_ENV_RE = /^__PAPAN_CRED_(GITHUB|GITLAB|BITBUCKET)_[A-Za-z0-9]{1,12}$/

/**
 * The platform's own `__PAPAN_*` namespace — never a credential's: `__PAPAN_GIT_ROUTES` is the git credential helper's
 * routing table (which bound credential answers which host), so a field mapped onto it would steer later git requests
 * to a host of the user's choosing with another credential. The one exception is a name in EXACTLY the shape the
 * platform generates for hidden (git) credentials (`GENERATED_HIDDEN_CRED_ENV_RE`).
 *
 * Accepted: the shape is not proof of a git binding. A session's own user may map a key THEY can read onto an existing
 * hidden name, which replaces that binding's git token for their own turn — no other token is read, no keybook access
 * is skipped, no host is redirected; it is the person choosing a different credential for their own session.
 */
function isPlatformReservedCredentialEnvName(name: string): boolean {
  return RESERVED_SANDBOX_ENV.has(name) || (name.startsWith('__PAPAN_') && !GENERATED_HIDDEN_CRED_ENV_RE.test(name))
}

/**
 * A name no credential may hold (#1500): the platform's own (above), and every name that steers the process itself —
 * `BASH_ENV` / `ENV` (a script bash sources before each command), `PATH`, the loader (`LD_*`), an interpreter's start-up
 * (`NODE_OPTIONS`, `PYTHON*`, `PERL5OPT`…) and the process's identity (`HOME`, `USER`, `SHELL`, `TMPDIR`) — the SAME
 * list a PDApp's own variables refuse (`isProcessSteeringEnvName`). A credential carries a secret; it never changes
 * which program runs. A credential already mapped onto one is refused where it resolves (`credentialEnvNameError`
 * in the keybook's per-entry resolution) and the person is told to pick it again under another name.
 */
export function isReservedCredentialEnvName(name: string): boolean {
  return isPlatformReservedCredentialEnvName(name) || isProcessSteeringEnvName(name)
}

/**
 * Why a credential's `envMapping` (field → env name) can't be used, or null when every name is usable — THE check
 * every path that takes a credential's env names passes (the session credentials route, the cred_to_env answer —
 * live or recorded for a resume — and the keybook's per-entry resolution, which a resumed turn's stored answer and
 * every turn's session credentials go through).
 *
 * The SAME rule on every backend, dockerode included, deliberately (review round 6): Docker accepts names the broker
 * refuses (`MY-KEY`, `1ST`), and one name rule everywhere means a host's switch to the broker can't start failing
 * credentials that worked the day before — a name the broker would refuse is refused at entry on every host.
 */
export function credentialEnvNameError(envMapping: unknown): string | null {
  if (envMapping === undefined || envMapping === null) return null
  if (typeof envMapping !== 'object' || Array.isArray(envMapping)) return 'envMapping must map field names to environment variable names'
  for (const v of Object.values(envMapping as Record<string, unknown>)) {
    if (typeof v === 'string' && SANDBOX_ENV_NAME_RE.test(v) && isReservedCredentialEnvName(v)) return reservedCredentialEnvNameMessage(v)
    if (typeof v !== 'string' || !SANDBOX_ENV_NAME_RE.test(v)) {
      return `${JSON.stringify(String(v).slice(0, 64))} is not a valid environment variable name (letters, digits and _, not starting with a digit, at most 128 characters)`
    }
  }
  return null
}

/** The refusal of a reserved name — says why, and the name to map it to instead (the one the generator would make). */
export function reservedCredentialEnvNameMessage(name: string): string {
  const why = isPlatformReservedCredentialEnvName(name)
    ? 'is reserved for the sandbox and can\'t hold a credential'
    : 'is reserved: it changes how programs run in the sandbox, so it can\'t hold a credential'
  return `${name} ${why} — map the credential to another name, such as ${finalizeEnvName(name)}`
}

// ─── Credential env-name GENERATION (review round 7) ───────────────────────────────────────────────────────────────
// THE generator of a credential's env names — the worker's cred_to_env tool (what the model is told), the browser's
// pending session row (what the card submits) and the keybook's auto-resolve all call it, so the name the model uses
// is the name the session binds. Every name it returns passes `credentialEnvNameError` (pinned by a test); the 400
// at the routes stays as the server-side backstop for a client that builds its own.

/** Longest name the broker accepts (kinds.envKeyRe: 1 + 127). */
const MAX_ENV_NAME = 128

/** Upper-case, every character outside [A-Z0-9_] → `_`, runs of `_` collapsed, trimmed; a leading digit gets `_`. A
 *  fragment: compose it, then `finalizeEnvName`. */
export function sanitizeEnvName(name: string): string {
  const cleaned = name.toUpperCase().replace(/[^A-Z0-9_]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '')
  return /^[0-9]/.test(cleaned) ? `_${cleaned}` : cleaned
}

/** FNV-1a (32 bit) — a stable, dependency-free tail for a truncated name (browser and worker get the same one). */
function fnv1a(s: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0 }
  return h.toString(16).toUpperCase().padStart(8, '0')
}

/**
 * A composed name made usable: sanitized again (a composition can't break the rule), empty → `fallback`, a reserved
 * name moved aside (`CRED_…` — a PREFIX, #1500: `LD_PRELOAD_CRED` would still be a loader name), and a name past 128
 * characters cut STABLY — its head plus a hash of the whole name, so
 * two long names that share a head stay distinct and the same input always gives the same name.
 */
export function finalizeEnvName(name: string, fallback = 'CRED'): string {
  let n = sanitizeEnvName(name) || fallback
  if (isReservedCredentialEnvName(n)) n = `CRED_${n}`
  if (n.length > MAX_ENV_NAME) n = `${n.slice(0, MAX_ENV_NAME - 9).replace(/_+$/, '')}_${fnv1a(n)}`
  return n
}

/** A name made usable WITHOUT rewriting a valid one (review round 9): a name the rule accepts is returned as is —
 *  `http_proxy` stays lower-case (curl ignores `HTTP_PROXY` for http://) —, anything else through `finalizeEnvName`. */
export function usableEnvName(name: string): string {
  return isUsableEnvName(name) ? name : finalizeEnvName(name)
}

/** One name passes the credential rule (a name the broker accepts, not reserved — `credentialEnvNameError`'s rule). */
export function isUsableEnvName(name: unknown): name is string {
  return typeof name === 'string' && SANDBOX_ENV_NAME_RE.test(name) && !isReservedCredentialEnvName(name)
}

/** Build a descriptive env var name like `GITHUB_PAPANAI_PAPAN_TOKEN` from the available context.
 *
 *  Naming rule (in priority order for the prefix):
 *    1. provider + context (e.g. 'github' + 'papan-ai/papan' → 'GITHUB_PAPAN_AI_PAPAN').
 *    2. provider + account_login from the resolved entry (keybook auto-resolve only).
 *    3. provider alone.
 *    4. filePath basename (e.g. '/papan/sales.fdb' → 'SALES').
 *    5. 'CRED'.
 *  The suffix is the agent-declared field name ('TOKEN', 'PAT', etc.); `includeLabel` (mode='new_env') interpolates
 *  the label to tell several entries for the same provider+context apart. */
export function generateCredEnvName(opts: {
  fieldName: string
  filePath?: string
  label?: string
  provider?: string
  contextRepo?: string
  /** Account from the resolved entry's data (e.g. installation owner). */
  accountLogin?: string
  /** Append the label as an extra disambiguator. */
  includeLabel?: boolean
}): string {
  let prefix: string
  if (opts.provider) {
    const providerPart = sanitizeEnvName(opts.provider)
    let discriminator = ''
    if (opts.contextRepo) {
      const [owner, name] = opts.contextRepo.split('/')
      const ownerPart = sanitizeEnvName(owner ?? '')
      const namePart = name ? sanitizeEnvName(name) : ''
      discriminator = ownerPart + (namePart ? `_${namePart}` : '')
    } else if (opts.accountLogin) {
      discriminator = sanitizeEnvName(opts.accountLogin)
    }
    prefix = discriminator ? `${providerPart}_${discriminator}` : providerPart
  } else if (opts.filePath) {
    const basename = opts.filePath.split('/').pop() || opts.filePath
    prefix = sanitizeEnvName(basename.replace(/\.[^.]+$/, '')) || 'CRED'
  } else {
    prefix = 'CRED'
  }
  const labelPart = opts.includeLabel && opts.label ? `_${sanitizeEnvName(opts.label)}` : ''
  const suffix = sanitizeEnvName(opts.fieldName) || 'VALUE'
  return finalizeEnvName(`${prefix || 'CRED'}${labelPart}_${suffix}`)
}

/** One declared field's env name: the agent's suggestion — kept as is when valid (`http_proxy`, round 9), else
 *  sanitized and finalized when anything survives — else the generated one. */
export function credFieldEnvName(field: { name: string; env?: string }, ctx: Omit<Parameters<typeof generateCredEnvName>[0], 'fieldName'>): string {
  return field.env && sanitizeEnvName(field.env) ? usableEnvName(field.env) : generateCredEnvName({ ...ctx, fieldName: field.name })
}

// ─── A PDApp's own variables (0326) ────────────────────────────────────────────────────────────────────────────────
// Names an app's Variables and secrets may NOT use: they steer the process itself, not the app's code — the loader,
// libc, the shells, an interpreter's start-up, where programs are found. The broker refuses them for every other kind
// (`services/papan-sandboxd/internal/kinds/exec.go` LoaderEnv / LoaderEnvPrefixes — keep the two in step) but lets the
// `sandbox` kind through, so for an app's env this is the only guard. Plus the platform's (`PAPAN_*`) and the identity
// of the process (HOME, USER, SHELL, TMPDIR). `GIT_*` as a family stays allowed (`GIT_TOKEN` is a normal name); only the
// names below and `GIT_CONFIG_*` are reserved, because git runs or loads what they name (`GIT_CONFIG_COUNT` / `_KEY_n` /
// `_VALUE_n` carry the sandbox's own `core.symlinks=false`; `_GLOBAL`, `_SYSTEM`, `_PARAMETERS` load config naming programs).
// The editor and pager names are reserved for the same reason: with GIT_EDITOR / GIT_PAGER unset git runs VISUAL / EDITOR
// / PAGER (`git commit` without `-m` opens the editor with no terminal), man runs MANPAGER, less runs LESSOPEN /
// LESSCLOSE. BROWSER stays allowed (the user, 2026-11-02): an ordinary app variable name that little in the sandbox runs.
export const APP_ENV_RESERVED_NAMES: ReadonlySet<string> = new Set([
  'PATH', 'IFS', 'ENV', 'BASH_ENV', 'PS4', 'SHELLOPTS', 'BASHOPTS', 'CDPATH', 'GLOBIGNORE', 'PROMPT_COMMAND',
  'GCONV_PATH', 'LOCPATH', 'NLSPATH', 'HOSTALIASES', 'RESOLV_HOST_CONF', 'GLIBC_TUNABLES', 'NODE_OPTIONS', 'NODE_PATH',
  'PERL5OPT', 'PERL5LIB', 'PERLLIB', 'PERL5DB', 'RUBYOPT', 'RUBYLIB', 'TCLLIBPATH', 'JAVA_TOOL_OPTIONS', '_JAVA_OPTIONS',
  'OPENSSL_CONF', 'OPENSSL_ENGINES', 'HOME', 'USER', 'SHELL', 'TMPDIR',
  'GIT_SSH', 'GIT_SSH_COMMAND', 'GIT_ASKPASS', 'SSH_ASKPASS', 'GIT_EXEC_PATH', 'GIT_PAGER', 'GIT_EDITOR',
  'GIT_SEQUENCE_EDITOR', 'GIT_EXTERNAL_DIFF', 'GIT_PROXY_COMMAND', 'GIT_TEMPLATE_DIR', 'GIT_DIR', 'GIT_COMMON_DIR',
  'GIT_WORK_TREE', 'EDITOR', 'VISUAL', 'PAGER', 'MANPAGER', 'LESSOPEN', 'LESSCLOSE',
])
export const APP_ENV_RESERVED_PREFIXES: readonly string[] = ['PAPAN_', 'LD_', 'DYLD_', 'PYTHON', 'BASH_FUNC_', 'MALLOC_', 'GIT_CONFIG_']

/** A name that steers the process itself (the lists above) — THE one predicate a PDApp's variables and a credential's
 *  env names both refuse (#1500). Case-insensitive, as the app rule has always been. */
export function isProcessSteeringEnvName(name: string): boolean {
  const upper = name.toUpperCase()
  return APP_ENV_RESERVED_NAMES.has(upper) || APP_ENV_RESERVED_PREFIXES.some(p => upper.startsWith(p))
}

/** Why a name can't be one of an app's variables, or null — the sandbox rule plus the reserved names above. */
export function appEnvNameError(name: unknown): string | null {
  if (typeof name !== 'string' || !SANDBOX_ENV_NAME_RE.test(name) || RESERVED_SANDBOX_ENV.has(name)) {
    return 'A variable name uses letters, digits and _, does not start with a digit, and is at most 128 characters'
  }
  if (isProcessSteeringEnvName(name)) {
    return `${name} is reserved: it would change how the platform runs the app, not only the app's code`
  }
  return null
}
